import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import Carousel from 'Components/Carousel';
import { Image, type StaticImageData } from 'Components/Image';
import Spacer from 'Components/Spacer';
import { breakpoints } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';

import { beforeAndAfter } from 'assets/content/landingPages/skincare';

import useMedia from 'utils/useMedia';
import useResponsiveVariant from 'utils/useResponsiveVariant';

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 100vw;
  padding: 56px 0 40px;

  background-color: ${theme.colors.neutral[200]};
`;

const CarouselContent = styled.div`
  position: relative;

  display: flex;
  justify-content: space-between;

  width: 100%;
  padding-top: ${theme.spacing['6x']};
`;

const Title = styled(Typography)`
  color: ${theme.colors.primary['300']};
`;

const Subtitle = styled(Typography)`
  padding: 0 ${legacyTheme.spacing.s24};
  color: ${theme.colors.primary['300']};
`;

const Disclaimer = styled(Typography)`
  padding: 0 32px;
  color: ${theme.colors.primary['300']};
  opacity: 0.5;
`;

const CarouselImage = styled(Image)`
  width: 49.9%;
  height: auto;
`;

type BeforeAndAfterSectionProps = {
  content?: {
    title: string;
    subtitle: string;
    results: Array<{
      title: string;
      beforeImage: StaticImageData;
      beforeImageAlt: string;
      afterImage: StaticImageData;
      afterImageAlt: string;
    }>;
    disclaimer: string;
  };
};

export const BeforeAndAfterSection = ({ content = beforeAndAfter }: BeforeAndAfterSectionProps) => {
  const { isMobile, isDesktop } = useResponsiveVariant();
  const isLargeScreen = useMedia(`(min-width: ${breakpoints.lg}px)`);

  return (
    <Container data-testid="skincare-lp-before-and-after">
      <Title align="center" medium variant="mono1">
        {content.title}
      </Title>
      <Spacer size={12} />

      <Subtitle align="center" markupName="h2" variant="h1r">
        {content.subtitle}
      </Subtitle>
      <Spacer size={isMobile ? 32 : 46} />
      <Carousel
        arrows={false}
        bigArrows={isDesktop}
        sectionsTitlesControl={content.results.map((result) => ({ title: result.title }))}
        width={isLargeScreen ? '870px' : isDesktop ? '600px' : '100%'}
      >
        {content.results.map((result) => (
          <CarouselContent
            key={result.title}
            data-testid={`before-and-after-pictures-${result.title}`}
          >
            <CarouselImage alt={result.beforeImageAlt} src={result.beforeImage} />
            <CarouselImage alt={result.afterImageAlt} src={result.afterImage} />
          </CarouselContent>
        ))}
      </Carousel>
      <Spacer size={isMobile ? 36 : 44} />

      <Disclaimer align="center" variant="p3">
        {content.disclaimer}
      </Disclaimer>
    </Container>
  );
};
